/**
 * Centralized Query Keys Factory
 *
 * User-scoped query keys to ensure proper cache isolation between users.
 * Including userId in query keys prevents stale data from appearing when
 * switching between user accounts.
 */

/**
 * Query keys for user-specific data (require userId prefix)
 */
export const queryKeys = {
  // Notifications
  notifications: {
    all: (userId: string | null) => [userId, 'notifications'] as const,
    list: (userId: string | null, params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
      [userId, 'notifications', 'list', params] as const,
    unreadCount: (userId: string | null) => [userId, 'notifications', 'unread-count'] as const,
  },

  // Notification Rules
  rules: {
    all: (userId: string | null) => [userId, 'notification-rules'] as const,
    lists: (userId: string | null) => [userId, 'notification-rules', 'list'] as const,
    list: (userId: string | null) => [userId, 'notification-rules', 'list'] as const,
    details: (userId: string | null) => [userId, 'notification-rules', 'detail'] as const,
    detail: (userId: string | null, id: string) => [userId, 'notification-rules', 'detail', id] as const,
    preview: (userId: string | null, ruleType: string, configHash: string) =>
      [userId, 'notification-rules', 'preview', ruleType, configHash] as const,
    breakdown: (userId: string | null, ruleType: string, configHash: string) =>
      [userId, 'notification-rules', 'breakdown', ruleType, configHash] as const,
  },

  // Inbox
  inbox: {
    all: (userId: string | null) => [userId, 'inbox'] as const,
    list: (userId: string | null, params?: { page?: number; limit?: number; messageType?: string | null; searchQuery?: string; readStatus?: string }) =>
      [userId, 'inbox', 'list', params] as const,
    detail: (userId: string | null, id: string) =>
      [userId, 'inbox', 'detail', id] as const,
    recent: (userId: string | null, limit?: number) =>
      [userId, 'inbox', 'recent', limit] as const,
    unreadCount: (userId: string | null) => [userId, 'inbox', 'unread-count'] as const,
    stats: (userId: string | null) => [userId, 'inbox', 'stats'] as const,
  },

  // Profile (user-specific but typically fetched via AuthContext)
  profile: (userId: string | null) => [userId, 'profile'] as const,

  // Bookmarks
  bookmarks: (userId: string | null) => [userId, 'bookmarks'] as const,

  // Alert Timeline (dashboard chart)
  alertTimeline: {
    all: (userId: string | null) => [userId, 'alert-timeline'] as const,
    chart: (userId: string | null, params?: { ruleIds?: string[]; days?: number }) =>
      [userId, 'alert-timeline', 'chart', params] as const,
  },
}

/**
 * Query keys for public/shared data (no userId prefix)
 */
export const publicQueryKeys = {
  // Publications (public data, same for all users)
  publications: {
    all: () => ['publications'] as const,
    list: (params?: object) => ['publications', 'list', params] as const,
    detail: (id: string) => ['publications', 'detail', id] as const,
    search: (params?: object) => ['publications', 'search', params] as const,
  },

  // Reference data (public)
  institutionCategories: () => ['institution-categories'] as const,
  publicationTypes: () => ['publication-types'] as const,
}
